/**
 * Coerce a backend `isVerified` to a strict boolean. Backends disagree on the
 * type: the EF response assembler casts to a real boolean (`filter_var`), but
 * the network getSettings endpoint returns the raw DB tinyint (`1` / `"1"`).
 * The hook gates the USPS-Verified badge on `isVerified === true`, so every
 * surface must feed it a boolean. Only the genuine truthy shapes count — never
 * a blanket `Boolean()`, which would read the string `"0"` as verified.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isAddressVerified(value) {
  return value === true || value === 1 || value === "1";
}

/**
 * Build the `value` prop that {@link useAddressAutocomplete} consumes from a
 * surface's own address state.
 *
 * The hook speaks a fixed {@link AddressValue} shape with an ISO country code.
 * Surfaces persist their own field names and (usually) a numeric country ID,
 * so each maps its state through this helper at the integration boundary. This
 * is the single source of truth for the hook's input contract — when the hook
 * gains or drops a field, only this function changes instead of all surfaces.
 *
 * Surfaces that store the street under a different key (e.g. `street`) must map
 * it onto `addr1` before calling, e.g. `toHookValue({ ...values, addr1: values.street }, iso)`.
 *
 * @param {Partial<AddressValue> & Record<string, unknown>} [src] - the
 *   surface's address state (any extra keys are ignored).
 * @param {string} countryISO - ISO code (e.g. "US"), derived from the surface's
 *   numeric country ID via `COUNTRY_OPTIONS_BY_ID`.
 * @returns {AddressValue}
 */
export default function toHookValue(src, countryISO) {
  return {
    addr1: src?.addr1 || "",
    city: src?.city || "",
    state: src?.state || "",
    zip: src?.zip || "",
    plus4: src?.plus4 || "",
    country: countryISO,
    isVerified: isAddressVerified(src?.isVerified),
    verificationProvider: src?.verificationProvider || "",
    verifiedAt: src?.verifiedAt || "",
    validationSkipped: src?.validationSkipped || "",
  };
}
